import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const About: React.FC = () => {
  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-24">
        <div className="relative z-10 mx-auto max-w-6xl text-center">
          <h1 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent md:text-6xl">
            About kol.my
          </h1>
          <p className="mx-auto mb-8 max-w-4xl text-xl leading-relaxed text-gray-400 md:text-2xl">
            Empowering brands across Southeast Asia with the future of influencer marketing. We connect visionary brands
            with authentic voices that inspire.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-3 font-semibold text-white transition-all hover:shadow-lg hover:shadow-violet-500/30"
            >
              Get Started Free <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-8 py-3 font-semibold transition-colors hover:bg-white/10"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-12 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-center text-4xl font-bold text-transparent">
            Our Mission
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-blue-500">
                <span className="text-3xl">Precision</span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-white">Precision Matching</h3>
              <p className="leading-relaxed text-gray-400">
                AI-powered algorithms that connect brands with the perfect KOLs for their unique needs and target
                audience.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-blue-500">
                <span className="text-3xl">Collab</span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-white">Seamless Collaboration</h3>
              <p className="leading-relaxed text-gray-400">
                End-to-end campaign management tools that simplify communication, contract handling, and performance
                tracking.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-blue-500">
                <span className="text-3xl">Data</span>
              </div>
              <h3 className="mb-2 text-xl font-bold text-white">Data-Driven Results</h3>
              <p className="leading-relaxed text-gray-400">
                Comprehensive analytics and ROI measurement to maximize every marketing Ringgit spent.
              </p>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-16">
        <div className="mx-auto max-w-6xl rounded-2xl bg-gradient-to-r from-violet-600 to-blue-600 p-8 text-white md:p-12">
          <h2 className="mb-12 text-center text-4xl font-bold">Our Story</h2>
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="mb-6 text-lg leading-relaxed">
                Founded in Kuala Lumpur in 2023, kol.my emerged from a simple observation: the influencer marketing
                landscape in Southeast Asia was fragmented, inefficient, and often unfair to creators.
              </p>
              <p className="mb-6 text-lg leading-relaxed">
                We set out to build a platform that treats both brands and KOLs as partners in success. Our AI-powered
                matching system ensures authentic collaborations, while our comprehensive tools handle everything from
                initial outreach to final payment.
              </p>
              <p className="text-lg leading-relaxed">
                Today, kol.my serves over 100 brands across Malaysia, Singapore, and beyond, facilitating thousands of
                successful campaigns and empowering creators to focus on what they do best – creating amazing content.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-white/10 p-6 text-center backdrop-blur-sm">
                <div className="mb-2 text-4xl font-bold">100+</div>
                <div className="text-sm opacity-80">Brand Partners</div>
              </div>
              <div className="rounded-xl bg-white/10 p-6 text-center backdrop-blur-sm">
                <div className="mb-2 text-4xl font-bold">10K+</div>
                <div className="text-sm opacity-80">Campaigns Managed</div>
              </div>
              <div className="rounded-xl bg-white/10 p-6 text-center backdrop-blur-sm">
                <div className="mb-2 text-4xl font-bold">5M+</div>
                <div className="text-sm opacity-80">Audience Reached</div>
              </div>
              <div className="rounded-xl bg-white/10 p-6 text-center backdrop-blur-sm">
                <div className="mb-2 text-4xl font-bold">98%</div>
                <div className="text-sm opacity-80">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={4} className="px-6 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-4xl font-bold text-transparent">
            Ready to Transform Your Marketing?
          </h2>
          <p className="mb-8 text-xl text-gray-400">
            Join hundreds of brands already growing with kol.my. Start your free trial today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-4 text-lg font-semibold text-white transition-all hover:shadow-xl hover:shadow-violet-500/30"
            >
              Start Free Trial <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-8 py-4 text-lg font-semibold transition-colors hover:bg-white/10"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default About;
